import React from 'react';
import BarChart from '../charts/barChart';
import PieChart from '../charts/pieChart';

interface SummaryData {
  minutesSaved: {
    minsAvoided: number;
    minsAdded: number;
    inMins: number;
    inHrs: number;
    laborRate: number;
    costDeflected: number;
  };
  overnightsAvoided: {
    count: number;
    hotelStay: number;
    perDiem: number;
    costDeflected: number;
  };
  additionalFees: {
    forConnections: number;
    forCancellations: number;
    forDiversions: number;
    forLateDepartures: number;
    forLateArrivals: number;
    total: number;
  };
  closedIssuesCount: number;
  resolutionDetails: {
    pnrsUpdatedCount: number;
    flightsChangedCount: number;
    groundChangedCount: number;
    hotelChangedCount: number;
  };
}

interface IssueResolutionsAndTypes {
  resolutions: string[];
  cancellation: number[];
  connection: number[];
  lateDeparture: number[];
  lateArrival: number[];
  diverted: number[];
}

interface TravelersContacted {
  contactedPercentage: number;
  notContactedPercentage: number;
}

interface IssuesReportingSummaryProps {
  summaryData: SummaryData;
  issueResolutionsAndTypes: IssueResolutionsAndTypes;
  travelersContacted: TravelersContacted;
}

const IssuesReportingSummary: React.FC<IssuesReportingSummaryProps> = ({
  summaryData,
  issueResolutionsAndTypes,
  travelersContacted,
}) => {
  const resolutionData = {
    title: 'Trip Disruptions + Resolutions',
    categories: issueResolutionsAndTypes.resolutions,
    series: [
      { name: 'Cancellation', data: issueResolutionsAndTypes.cancellation },
      { name: 'Connection', data: issueResolutionsAndTypes.connection },
      { name: 'Late Departure', data: issueResolutionsAndTypes.lateDeparture },
      { name: 'Late Arrival', data: issueResolutionsAndTypes.lateArrival },
      { name: 'Diverted', data: issueResolutionsAndTypes.diverted },
    ],
  };

  const travelersContactedData = {
    categories: ['Yes', 'No'],
    series: [
      {
        name: 'Travelers Contacted',
        data: [travelersContacted.contactedPercentage, travelersContacted.notContactedPercentage],
      },
    ],
  };

  const isValueGreaterThanZero = (value: number) => value > 0;

  return (
    <div className="row">
      <div className="col-md-4">
        <DataPoint title="Trip Minutes Saved">
          <TripMinutesSaved summaryData={summaryData} isValueGreaterThanZero={isValueGreaterThanZero} />
        </DataPoint>
        <DataPoint title="Overnights Avoided">
          <OvernightsAvoided summaryData={summaryData} isValueGreaterThanZero={isValueGreaterThanZero} />
        </DataPoint>
        <DataPoint title="Additional Fees">
          <AdditionalFees summaryData={summaryData} isValueGreaterThanZero={isValueGreaterThanZero} />
        </DataPoint>
      </div>
      <div className="col-md-4">
        <DataPoint title={`Total Trip Disruptions ${summaryData.closedIssuesCount}`}>
          <TotalTripDisruptions summaryData={summaryData} />
        </DataPoint>
        <DataPoint title="Resolved Trip Disruptions">
          <BarChart reportData={resolutionData} height={396} bottomMargin={100} />
        </DataPoint>
      </div>
      <div className="col-md-4">
        <DataPoint title="Travelers Contacted">
          <PieChart reportData={travelersContactedData} height={360} radius={120} includeLegend={true} />
        </DataPoint>
        <DataPoint title="Notes">
          <Notes />
        </DataPoint>
      </div>
    </div>
  );
};


interface DataPointProps {
  title: string;
  children: React.ReactNode;
}

const DataPoint: React.FC<DataPointProps> = ({ title, children }) => (
  <div className="col-md-12 data-point data-point-bkg">
    <div className="point-title point-title-colored">{title}</div>
    {children}
  </div>
);

interface TripMinutesSavedProps {
  summaryData: SummaryData;
  isValueGreaterThanZero: (value: number) => boolean;
}

const TripMinutesSaved: React.FC<TripMinutesSavedProps> = ({ summaryData, isValueGreaterThanZero }) => (
  <>
    <div className="table-responsive">
      <table className="table table-striped table-hover report-table">
        <tbody>
          <tr>
            <td>Trip Minutes Avoided&nbsp;&sup2;</td>
            <td>{summaryData.minutesSaved.minsAvoided}</td>
          </tr>
          <tr>
            <td>Trip Minutes Added</td>
            <td className={isValueGreaterThanZero(summaryData.minutesSaved.minsAdded) ? 'negative-value' : ''}>
              {summaryData.minutesSaved.minsAdded}
            </td>
          </tr>
        </tbody>
      </table>
    </div>
    <div className="col-md-12">
      <div className="col-md-5 text-center">
        <div className={isValueGreaterThanZero(summaryData.minutesSaved.inMins) ? 'positive-value' : ''}>
          {summaryData.minutesSaved.inMins}
        </div>
        <div className="data-base">TRIP MINUTES SAVED</div>
      </div>
      <div className="col-md-2 data-base text-center">or</div>
      <div className="col-md-5 text-center">
        <div className={isValueGreaterThanZero(summaryData.minutesSaved.inHrs) ? 'positive-value' : ''}>
          {summaryData.minutesSaved.inHrs}
        </div>
        <div className="data-base">STAFF HOURS SAVED&nbsp;&sup3;</div>
      </div>
    </div>
    <div className="col-md-12">
      <div className="col-md-5 text-center">
        <div className="data-base-sub-1">x ${summaryData.minutesSaved.laborRate || 0}/hour labor rate</div>
      </div>
      <div className="col-md-2 data-base"></div>
      <div className="col-md-5 text-center">
        <div className="data-base-sub-2">${summaryData.minutesSaved.laborRate || 0}</div>
      </div>
    </div>
    <div className="col-md-12 data-base-total-sep"></div>
    <div className="col-md-12">
      <div className="col-md-5 text-center">
        <div className="data-base-total-label">STAFF COST DEFLECTED</div>
      </div>
      <div className="col-md-2 data-base"></div>
      <div className="col-md-5 text-center">
        <div className="data-base-total-value">
          ${isNaN(Number(summaryData.minutesSaved.costDeflected)) ? 0 : summaryData.minutesSaved.costDeflected}
        </div>
      </div>
    </div>
  </>
);

interface OvernightsAvoidedProps {
  summaryData: SummaryData;
  isValueGreaterThanZero: (value: number) => boolean;
}

const OvernightsAvoided: React.FC<OvernightsAvoidedProps> = ({ summaryData, isValueGreaterThanZero }) => (
  <>
    <div className="col-md-12 text-center">
      <div className={isValueGreaterThanZero(summaryData.overnightsAvoided.count) ? 'positive-value' : ''}>
        {summaryData.overnightsAvoided.count}
      </div>
    </div>
    <div className="col-md-12 text-center">
      <div className="data-base">OVERNIGHTS AVOIDED</div>
    </div>
    <div className="col-md-12 data-base-sep"></div>
    <div className="col-md-12">
      <div className="col-md-5 text-center">
        <div className="data-base-sub-1">x avg. hotel stay<br />per diem</div>
      </div>
      <div className="col-md-2 data-base text-center">+</div>
      <div className="col-md-5 text-center">
        <div className="data-base-sub-2">${summaryData.overnightsAvoided.hotelStay || 0}<br />${summaryData.overnightsAvoided.perDiem || 0}</div>
      </div>
    </div>
    <div className="col-md-12 data-base-total-sep"></div>
    <div className="col-md-12">
      <div className="col-md-5 text-center">
        <div className="data-base-total-label">EST. HARD COST DEFLECTED</div>
      </div>
      <div className="col-md-2 data-base"></div>
      <div className="col-md-5 text-center">
      <div className="data-base-total-value">
        ${isNaN(Number(summaryData.overnightsAvoided.costDeflected)) ? 0 : summaryData.overnightsAvoided.costDeflected}
      </div>
      </div>
    </div>
  </>
);

interface AdditionalFeesProps {
  summaryData: SummaryData;
  isValueGreaterThanZero: (value: number) => boolean;
}

const AdditionalFees: React.FC<AdditionalFeesProps> = ({ summaryData, isValueGreaterThanZero }) => {
  const totalAdditionalFees =
    (summaryData.additionalFees.forConnections || 0) +
    (summaryData.additionalFees.forCancellations || 0) +
    (summaryData.additionalFees.forDiversions || 0) +
    (summaryData.additionalFees.forLateDepartures || 0) +
    (summaryData.additionalFees.forLateArrivals || 0);

  return (
    <>
      <div className="col-md-12">
        <div className="data-base-sub-1">all additional costs for rebooking, change fees, or purchase of entirely new ticket</div>
        <div className="table-responsive">
          <table className="table table-striped table-hover report-table">
            <tbody>
              <tr>
                <td>Connections</td>
                <td>&nbsp;</td>
                <td>&nbsp;</td>
                <td className={isValueGreaterThanZero(summaryData.additionalFees.forConnections) ? 'negative-value' : ''}>
                  ${summaryData.additionalFees.forConnections || 0}
                </td>
              </tr>
              <tr>
                <td>Cancellations</td>
                <td>&nbsp;</td>
                <td>&nbsp;</td>
                <td className={isValueGreaterThanZero(summaryData.additionalFees.forCancellations) ? 'negative-value' : ''}>
                  ${summaryData.additionalFees.forCancellations || 0}
                </td>
              </tr>
              <tr>
                <td>Diversions</td>
                <td>&nbsp;</td>
                <td>&nbsp;</td>
                <td className={isValueGreaterThanZero(summaryData.additionalFees.forDiversions) ? 'negative-value' : ''}>
                  ${summaryData.additionalFees.forDiversions || 0}
                </td>
              </tr>
              <tr>
                <td>Late Departures</td>
                <td>&nbsp;</td>
                <td>&nbsp;</td>
                <td className={isValueGreaterThanZero(summaryData.additionalFees.forLateDepartures) ? 'negative-value' : ''}>
                  ${summaryData.additionalFees.forLateDepartures || 0}
                </td>
              </tr>
              <tr>
                <td>Late Arrivals</td>
                <td>&nbsp;</td>
                <td>&nbsp;</td>
                <td className={isValueGreaterThanZero(summaryData.additionalFees.forLateArrivals) ? 'negative-value' : ''}>
                  ${summaryData.additionalFees.forLateArrivals || 0}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
      <div className="col-md-12 data-base-total-sep"></div>
      <div className="col-md-12">
        <div className="col-md-5 text-center">
          <div className="data-base-total-label">TOTAL ADDITIONAL FEES</div>
        </div>
        <div className="col-md-2 data-base"></div>
        <div className="col-md-5 text-center">
          <div className="data-base-total-value">${totalAdditionalFees}</div>
        </div>
      </div>
    </>
  );
};

interface TotalTripDisruptionsProps {
  summaryData: SummaryData;
}

const TotalTripDisruptions: React.FC<TotalTripDisruptionsProps> = ({ summaryData }) => (
  <div className="table-responsive">
    <table className="table table-striped table-hover report-table">
      <tbody>
        <tr>
          <td>PNRs Updated&nbsp;&sup1;</td>
          <td>{summaryData.resolutionDetails.pnrsUpdatedCount}</td>
        </tr>
        <tr>
          <td colSpan={2} className="spacer"></td>
        </tr>
        <tr>
          <td colSpan={2} className="data">We monitored your travelers' location through rebooking, cancellations,
            diversions, and delays.</td>
        </tr>
        <tr>
          <td>Flights Changed</td>
          <td>{summaryData.resolutionDetails.flightsChangedCount}</td>
        </tr>
        <tr>
          <td colSpan={2} className="spacer"></td>
        </tr>
        <tr>
          <td colSpan={2} className="data">We changed flights on travelers' itineraries to recover from
            cancellations and missed connections.</td>
        </tr>
        <tr>
          <td>Ground Transportation Changed</td>
          <td>{summaryData.resolutionDetails.groundChangedCount}</td>
        </tr>
        <tr>
          <td colSpan={2} className="spacer"></td>
        </tr>
        <tr>
          <td colSpan={2} className="data">We updated disrupted travelers' ground transportation to
            save time and money on arrival.</td>
        </tr>
        <tr>
          <td>Hotel Changed</td>
          <td>{summaryData.resolutionDetails.hotelChangedCount}</td>
        </tr>
        <tr>
          <td colSpan={2} className="spacer"></td>
        </tr>
        <tr>
          <td colSpan={2} className="data">We updated disrupted travelers' hotel reservations to save time
            and money on arrival.</td>
        </tr>
      </tbody>
    </table>
  </div>
);

const Notes: React.FC = () => (
  <div className="col-md-12 data-base-sub-note">
    <div className="col-md-1">1</div>
    <div className="col-md-11">Airlines are not consistent in updating reprotections in the GDS. Only through active trip disruption management can corporations and TMCs be assured of a traveler's itinerary and location.</div>
    <div className="col-md-1">2</div>
    <div className="col-md-11">Trip Minutes Avoided is an estimate of minutes avoided for the traveler through agent advocacy relative to what the airline would have done.</div>
    <div className="col-md-1">3</div>
    <div className="col-md-11">While an hour spent in an airport may not be completely wasted time, this report attempts to quantify hours that are sub-optimal for business efficiency.</div>
  </div>
);

export default IssuesReportingSummary;